import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useOrganization } from '../contexts/OrganizationContext'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { supabase } from '../services/supabase'
import { useToast } from '../contexts/ToastContext'
import Card from '../components/Card'
import {
  MessageSquare,
  Plus,
  Search,
  ThumbsUp,
  Clock,
  User,
  Lock,
  Sparkles,
  ArrowRight,
  Pin,
  Lock as LockIcon,
  HelpCircle,
} from 'lucide-react'
import { motion } from 'framer-motion'
import ForumHelp from './ForumHelp'

const Forum = () => {
  const { t } = useTranslation()
  const { organization } = useOrganization()
  const { tier, isPaid, isTrial } = useSubscriptionStatus()
  const { showError, success } = useToast()
  
  const [categories, setCategories] = useState([])
  const [topics, setTopics] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedTopic, setSelectedTopic] = useState(null)
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNewTopicModal, setShowNewTopicModal] = useState(false)
  const [showNewPostModal, setShowNewPostModal] = useState(false)
  const [newTopicData, setNewTopicData] = useState({ title: '', content: '', categoryId: '' })
  const [newPostContent, setNewPostContent] = useState('')
  const [showHelp, setShowHelp] = useState(false)
  const [userCache, setUserCache] = useState({}) // Cache for user display names
  const [currentUserId, setCurrentUserId] = useState(null)

  // Check if user has access to forum based on tier
  const hasForumAccess = isPaid || isTrial || tier === 'demo'

  useEffect(() => {
    // Get current user ID
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        setCurrentUserId(user.id)
        // Cache current user's name
        setUserCache(prev => ({
          ...prev,
          [user.id]: user.user_metadata?.name || user.email?.split('@')[0] || 'You'
        }))
      }
    }
    getCurrentUser()
  }, [])

  useEffect(() => {
    if (hasForumAccess) {
      loadCategories()
      loadTopics()
    }
  }, [hasForumAccess, selectedCategory])

  useEffect(() => {
    if (selectedTopic) {
      loadPosts()
    }
  }, [selectedTopic])

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('forum_categories')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true })

      if (error) throw error
      setCategories(data || [])
    } catch (error) {
      console.error('Error loading categories:', error)
      showError('Failed to load forum categories')
    }
  }

  const loadTopics = async () => {
    try {
      let query = supabase
        .from('forum_topics')
        .select(`
          *,
          category:forum_categories(*)
        `)
        .order('is_pinned', { ascending: false })
        .order('last_reply_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })

      if (selectedCategory) {
        query = query.eq('category_id', selectedCategory)
      }

      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`)
      }

      const { data, error } = await query.limit(50)

      if (error) throw error
      setTopics(data || [])
    } catch (error) {
      console.error('Error loading topics:', error)
      showError('Failed to load forum topics')
    } finally {
      setLoading(false)
    }
  }

  const loadPosts = async () => {
    if (!selectedTopic) return

    try {
      const { data, error } = await supabase
        .from('forum_posts')
        .select(`
          *,
          reactions:forum_reactions(*)
        `)
        .eq('topic_id', selectedTopic.id)
        .order('created_at', { ascending: true })

      if (error) throw error
      setPosts(data || [])
    } catch (error) {
      console.error('Error loading posts:', error)
      showError('Failed to load forum posts')
    }
  }

  const handleCreateTopic = async () => {
    if (!newTopicData.title.trim() || !newTopicData.content.trim() || !newTopicData.categoryId) {
      showError('Please fill in all fields')
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('forum_topics')
        .insert({
          category_id: newTopicData.categoryId,
          user_id: user.id,
          organization_id: organization?.id,
          title: newTopicData.title,
          content: newTopicData.content,
        })
        .select()
        .single()

      if (error) throw error

      success('Topic created successfully!')
      setShowNewTopicModal(false)
      setNewTopicData({ title: '', content: '', categoryId: '' })
      loadTopics()
    } catch (error) {
      console.error('Error creating topic:', error)
      showError(error.message || 'Failed to create topic')
    }
  }

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) {
      showError('Please enter a reply')
      return
    }

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('forum_posts')
        .insert({
          topic_id: selectedTopic.id,
          user_id: user.id,
          organization_id: organization?.id,
          content: newPostContent,
        })
        .select()
        .single()

      if (error) throw error

      success('Reply posted successfully!')
      setShowNewPostModal(false)
      setNewPostContent('')
      loadPosts()
      loadTopics() // Refresh to update reply count
    } catch (error) {
      console.error('Error creating post:', error)
      showError(error.message || 'Failed to post reply')
    }
  }

  const handleReaction = async (postId, reactionType = 'like') => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      // Check if reaction already exists
      const { data: existing } = await supabase
        .from('forum_reactions')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .eq('reaction_type', reactionType)
        .maybeSingle()

      if (existing) {
        // Remove reaction
        const { error } = await supabase
          .from('forum_reactions')
          .delete()
          .eq('id', existing.id)

        if (error) throw error
      } else {
        // Add reaction
        const { error } = await supabase
          .from('forum_reactions')
          .insert({
            post_id: postId,
            user_id: user.id,
            reaction_type: reactionType,
          })

        if (error) throw error
      }

      loadPosts()
    } catch (error) {
      console.error('Error toggling reaction:', error)
    }
  }

  // Load user names for topics and posts
  useEffect(() => {
    const loadUserNames = async () => {
      if (!organization?.id) return
      
      // Collect all unique user IDs from topics and posts
      const userIds = new Set()
      topics.forEach(topic => {
        if (topic.user_id) userIds.add(topic.user_id)
        if (topic.last_reply_by) userIds.add(topic.last_reply_by)
      })
      posts.forEach(post => {
        if (post.user_id) userIds.add(post.user_id)
      })
      
      // Remove current user and already cached users
      const userIdsToFetch = Array.from(userIds).filter(
        id => id !== currentUserId && !userCache[id]
      )
      
      if (userIdsToFetch.length === 0) return
      
      // For now, set generic names (can be improved with a user lookup service)
      const newCache = { ...userCache }
      userIdsToFetch.forEach(userId => {
        newCache[userId] = 'Forum User'
      })
      setUserCache(newCache)
    }
    
    if (topics.length > 0 || posts.length > 0) {
      loadUserNames()
    }
  }, [topics, posts, organization, currentUserId])
  
  const getUserDisplay = (userId) => {
    if (!userId) return 'Anonymous'
    
    // If it's the current user, show "You"
    if (userId === currentUserId) {
      return 'You'
    }
    
    // Return from cache or fallback
    return userCache[userId] || 'Forum User'
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  if (!hasForumAccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card variant="soft" className="p-8 text-center">
          <LockIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Forum Access Required
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The forum is available to all subscribers. Please upgrade your subscription to access the community forum.
          </p>
          <a
            href="/organization"
            className="btn-primary inline-flex items-center gap-2"
          >
            View Subscription Plans
            <ArrowRight size={18} />
          </a>
        </Card>
      </div>
    )
  }

  if (selectedTopic) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Topic Header */}
        <div className="mb-6">
          <button
            onClick={() => {
              setSelectedTopic(null)
              setPosts([])
            }}
            className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center gap-2"
          >
            ← Back to Forum
          </button>
          <Card variant="soft" className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {selectedTopic.is_pinned && (
                    <Pin className="w-4 h-4 text-primary-600" />
                  )}
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedTopic.title}
                  </h1>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <User size={14} />
                      {getUserDisplay(selectedTopic.user_id)}
                    </span>
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {formatDate(selectedTopic.created_at)}
                  </span>
                  <span>{selectedTopic.reply_count} replies</span>
                  <span>{selectedTopic.view_count} views</span>
                </div>
              </div>
            </div>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {selectedTopic.content}
              </p>
            </div>
          </Card>
        </div>

        {/* Posts/Replies */}
        <div className="space-y-4 mb-6">
          {posts.map((post) => (
            <Card key={post.id} variant="soft" className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                    <User size={20} className="text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {getUserDisplay(post.user_id)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(post.created_at)}
                    </p>
                  </div>
                </div>
                {post.is_solution && (
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-full text-sm font-medium">
                    Solution
                  </span>
                )}
              </div>
              <div className="prose dark:prose-invert max-w-none mb-4">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {post.content}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleReaction(post.id, 'like')}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <ThumbsUp size={18} />
                  <span>
                    {post.reactions?.filter((r) => r.reaction_type === 'like').length || 0}
                  </span>
                </button>
              </div>
            </Card>
          ))}
        </div>

        {/* Reply Form */}
        <Card variant="soft" className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Post a Reply
          </h3>
          <textarea
            value={newPostContent}
            onChange={(e) => setNewPostContent(e.target.value)}
            placeholder="Write your reply..."
            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[120px] mb-4"
          />
          <button
            onClick={handleCreatePost}
            className="btn-primary"
          >
            Post Reply
          </button>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Community Forum
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Connect with other practitioners, share knowledge, and get help
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowHelp(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Help & Guide"
          >
            <HelpCircle size={18} />
            <span className="hidden sm:inline">Help</span>
          </button>
          <button
            onClick={() => setShowNewTopicModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            New Topic
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search topics..."
            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {categories.map((category) => (
          <motion.div
            key={category.id}
            whileHover={{ scale: 1.02 }}
            onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}
            className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
              selectedCategory === category.id
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {category.name}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {category.description}
            </p>
            {category.required_tier && (
              <span className="inline-block px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs">
                {category.required_tier} tier
              </span>
            )}
          </motion.div>
        ))}
      </div>

      {/* Topics List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading topics...</p>
        </div>
      ) : topics.length === 0 ? (
        <Card variant="soft" className="p-12 text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No topics yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Be the first to start a discussion!
          </p>
          <button
            onClick={() => setShowNewTopicModal(true)}
            className="btn-primary"
          >
            Create First Topic
          </button>
        </Card>
      ) : (
        <div className="space-y-4">
          {topics.map((topic) => (
            <Card
              key={topic.id}
              variant="soft"
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedTopic(topic)
                // Increment view count
                supabase
                  .from('forum_topics')
                  .update({ view_count: (topic.view_count || 0) + 1 })
                  .eq('id', topic.id)
                  .then(() => loadTopics())
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {topic.is_pinned && (
                      <Pin className="w-4 h-4 text-primary-600" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {topic.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span className="flex items-center gap-1">
                      <User size={14} />
                      {getUserDisplay(topic.user_id)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatDate(topic.created_at)}
                    </span>
                    <span>{topic.category?.name}</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 line-clamp-2">
                    {topic.content}
                  </p>
                </div>
                <div className="ml-4 text-center">
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {topic.reply_count || 0}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    replies
                  </div>
                </div>
              </div>
              {topic.last_reply_at && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
                  Last reply {formatDate(topic.last_reply_at)} by{' '}
                  {getUserDisplay(topic.last_reply_by)}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* New Topic Modal */}
      {showNewTopicModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Create New Topic
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={newTopicData.categoryId}
                  onChange={(e) => setNewTopicData({ ...newTopicData, categoryId: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={newTopicData.title}
                  onChange={(e) => setNewTopicData({ ...newTopicData, title: e.target.value })}
                  placeholder="Topic title..."
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Content
                </label>
                <textarea
                  value={newTopicData.content}
                  onChange={(e) => setNewTopicData({ ...newTopicData, content: e.target.value })}
                  placeholder="Write your topic..."
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-h-[200px]"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-4 mt-6">
              <button
                onClick={() => {
                  setShowNewTopicModal(false)
                  setNewTopicData({ title: '', content: '', categoryId: '' })
                }}
                className="px-6 py-3 rounded-xl font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTopic}
                className="btn-primary"
              >
                Create Topic
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && (
        <ForumHelp onClose={() => setShowHelp(false)} />
      )}
    </div>
  )
}

export default Forum
